// Users Tab Component for AdminPage
import React, { useState } from 'react';
import { Plus, Edit, Trash2, Key } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { formatPhone, handlePhoneInput } from './constants';
import * as api from '../../services/api';

const UsersTab = ({ users, setUsers, roles, currentUser }) => {
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState(null);
  const [passwordForm, setPasswordForm] = useState({ new_password: '', confirm_password: '' });
  const [passwordError, setPasswordError] = useState('');
  const [newUser, setNewUser] = useState({
    name: '', email: '', password: '', role: 'vendedor', 
    commission_rate: 35, phone: '', position: '', supervisor_id: ''
  });

  const handleAddUser = async () => {
    try {
      const response = await api.createUser(newUser);
      setUsers([...users, response.data]);
      setShowAddUserModal(false);
      setNewUser({ name: '', email: '', password: '', role: 'vendedor', commission_rate: 35, phone: '', position: '', supervisor_id: '' });
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Error al crear usuario: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEditUser = async () => {
    try {
      const updateData = { ...selectedUser };
      delete updateData._id;
      const response = await api.updateUser(selectedUser.id, updateData);
      setUsers(users.map(u => u.id === selectedUser.id ? response.data : u));
      setShowEditUserModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Error al actualizar usuario: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este usuario?')) {
      try {
        await api.deleteUser(id);
        setUsers(users.filter(u => u.id !== id));
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Error al eliminar usuario: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const openEditModal = (user) => {
    setSelectedUser({ ...user });
    setShowEditUserModal(true);
  };

  const openPasswordModal = (user) => {
    setPasswordTarget(user);
    setPasswordForm({ new_password: '', confirm_password: '' });
    setPasswordError('');
    setShowPasswordModal(true);
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    if (!passwordForm.new_password) {
      setPasswordError('La nueva contraseña es requerida');
      return;
    }
    if (passwordForm.new_password.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    try {
      await api.changePassword(passwordTarget.id, { new_password: passwordForm.new_password, is_admin: true });
      setShowPasswordModal(false);
      setPasswordTarget(null);
      alert('Contraseña actualizada correctamente');
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError(error.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-gray-700">Usuarios del Sistema</CardTitle>
          <Button onClick={() => setShowAddUserModal(true)} className="bg-[#D4A843] hover:bg-[#c49a3a]" data-testid="add-user-btn">
            <Plus size={18} className="mr-2" />
            Agregar Usuario
          </Button>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b">
                  <th className="pb-3 font-medium text-gray-600">Usuario</th>
                  <th className="pb-3 font-medium text-gray-600">Email</th>
                  <th className="pb-3 font-medium text-gray-600">Teléfono</th>
                  <th className="pb-3 font-medium text-gray-600">Posición</th>
                  <th className="pb-3 font-medium text-gray-600">Rol</th>
                  <th className="pb-3 font-medium text-gray-600">Comisión</th>
                  <th className="pb-3 font-medium text-gray-600">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="py-3 px-2">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 rounded-full bg-[#D4A843] flex items-center justify-center text-white text-sm font-medium">
                          {user.avatar || user.name?.slice(0, 2).toUpperCase()}
                        </div>
                        <span className="font-medium text-gray-800">{user.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-2 text-sm text-gray-600">{user.email}</td>
                    <td className="py-3 px-2 text-sm text-gray-600">{formatPhone(user.phone) || '-'}</td>
                    <td className="py-3 px-2 text-sm text-gray-600">{user.position || '-'}</td>
                    <td className="py-3 px-2">
                      <Badge className={user.role === 'administrator' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}>
                        {user.role}
                      </Badge>
                    </td>
                    <td className="py-3 px-2 text-sm text-[#D4A843] font-medium">{user.commission_rate}%</td>
                    <td className="py-3 px-2">
                      <div className="flex items-center space-x-1">
                        <button onClick={() => openPasswordModal(user)} className="p-2 hover:bg-yellow-50 rounded-lg transition-colors" title="Cambiar contraseña" data-testid={`change-password-${user.id}`}>
                          <Key size={16} className="text-yellow-600" />
                        </button>
                        <button onClick={() => openEditModal(user)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" data-testid={`edit-user-${user.id}`}>
                          <Edit size={16} className="text-gray-500" />
                        </button>
                        <button onClick={() => handleDeleteUser(user.id)} className="p-2 hover:bg-red-50 rounded-lg transition-colors" data-testid={`delete-user-${user.id}`}>
                          <Trash2 size={16} className="text-red-500" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Add User Modal */}
      <Dialog open={showAddUserModal} onOpenChange={setShowAddUserModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Agregar Usuario</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Nombre</label>
              <Input value={newUser.name} onChange={(e) => setNewUser({...newUser, name: e.target.value})} placeholder="Nombre completo" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Email</label>
              <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} placeholder="email@empresa.com" />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Contraseña</label>
              <Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} placeholder="********" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Teléfono</label>
                <Input value={newUser.phone} onChange={(e) => setNewUser({...newUser, phone: handlePhoneInput(e.target.value)})} placeholder="(787) 555-0000" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Posición</label>
                <Input value={newUser.position} onChange={(e) => setNewUser({...newUser, position: e.target.value})} placeholder="Director, Vendedor..." />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Rol</label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser({...newUser, role: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="administrator">Administrador</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="vendedor">Vendedor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Comisión %</label>
                <Input type="number" value={newUser.commission_rate} onChange={(e) => setNewUser({...newUser, commission_rate: parseFloat(e.target.value)})} />
              </div>
            </div>
            {newUser.role === 'vendedor' && (
              <div>
                <label className="text-sm font-medium text-gray-700">Supervisor</label>
                <Select value={newUser.supervisor_id} onValueChange={(v) => setNewUser({...newUser, supervisor_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar supervisor" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Sin supervisor</SelectItem>
                    {users.filter(u => u.role === 'supervisor' || u.role === 'administrator').map(u => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddUserModal(false)}>Cancelar</Button>
            <Button onClick={handleAddUser} className="bg-[#D4A843] hover:bg-[#c49a3a]">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Modal */}
      <Dialog open={showEditUserModal} onOpenChange={setShowEditUserModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Usuario</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Nombre</label>
                <Input value={selectedUser.name} onChange={(e) => setSelectedUser({...selectedUser, name: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Email</label>
                <Input type="email" value={selectedUser.email} onChange={(e) => setSelectedUser({...selectedUser, email: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Teléfono</label>
                  <Input value={selectedUser.phone || ''} onChange={(e) => setSelectedUser({...selectedUser, phone: handlePhoneInput(e.target.value)})} placeholder="(787) 555-0000" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Posición</label>
                  <Input value={selectedUser.position || ''} onChange={(e) => setSelectedUser({...selectedUser, position: e.target.value})} placeholder="Director, Vendedor..." />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700">Rol</label>
                  <Select value={selectedUser.role} onValueChange={(v) => setSelectedUser({...selectedUser, role: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="administrator">Administrador</SelectItem>
                      <SelectItem value="supervisor">Supervisor</SelectItem>
                      <SelectItem value="vendedor">Vendedor</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Comisión %</label>
                  <Input type="number" value={selectedUser.commission_rate} onChange={(e) => setSelectedUser({...selectedUser, commission_rate: parseFloat(e.target.value)})} />
                </div>
              </div>
              {selectedUser.role === 'vendedor' && (
                <div>
                  <label className="text-sm font-medium text-gray-700">Supervisor</label>
                  <Select value={selectedUser.supervisor_id || ''} onValueChange={(v) => setSelectedUser({...selectedUser, supervisor_id: v})}>
                    <SelectTrigger><SelectValue placeholder="Seleccionar supervisor" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="">Sin supervisor</SelectItem>
                      {users.filter(u => u.role === 'supervisor' || u.role === 'administrator').map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditUserModal(false)}>Cancelar</Button>
            <Button onClick={handleEditUser} className="bg-[#D4A843] hover:bg-[#c49a3a]">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password Change Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cambiar Contraseña</DialogTitle>
          </DialogHeader>
          {passwordTarget && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">Cambiando contraseña para: <strong>{passwordTarget.name}</strong></p>
              <div>
                <label className="text-sm font-medium text-gray-700">Nueva Contraseña</label>
                <Input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})} placeholder="Mínimo 6 caracteres" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Confirmar Contraseña</label>
                <Input type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})} placeholder="Repite la contraseña" />
              </div>
              {passwordError && <p className="text-sm text-red-500">{passwordError}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(false)}>Cancelar</Button>
            <Button onClick={handleChangePassword} className="bg-[#D4A843] hover:bg-[#c49a3a]">Cambiar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UsersTab;
